/* eslint-disable no-use-before-define */
import * as _ from "underscore";
import React, { useEffect, useState } from "react";
import * as moment from "moment";
import { connect } from "react-redux";
import {
  Button,
  Checkbox,
  DatePicker,
  DatePickerInput,
  InlineNotification,
  Loading,
  Modal,
  RadioButton,
  RadioButtonGroup,
  Table as CarbonTable,
  TableBody,
  TableCell,
  TableRow,
} from "carbon-components-react";
import { Checkmark32, Save16 } from "@carbon/icons-react";

import BaseTable, { EditingState } from "web_modules/shared/BaseTable";
import {
  DocTypes,
  EmploymentShape,
  MilitaryServiceShape,
  UpdatePropertyTypeRequest,
} from "@eave/shared/types/api";
import { addressToPostalFormatStr } from "@eave/shared/utils/addresses";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import {
  LoadingState,
  IncomeType,
  EmploymentType,
  incomeTypeToStr,
} from "underwriter_dashboard/shared/types";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import APIClient from "web_modules/APIClient";
import { logException } from "web_modules/utils/sentry";
import { findFieldValueForSection } from "underwriter_dashboard/shared";

import LearnMore from "web_modules/shared/LearnMore";
import LabeledObject from "web_modules/shared/LabeledObject";
import { TableHeaderDataShape } from "web_modules/shared/BaseTable/types";

import { mapDispatchToProps } from "../selectors";
import EmploymentModalForm from "./EmploymentModalForm";
import { initialValues } from "./form-values";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");
const employmentHurdleStyles = require("./styles.module.scss");

/* EmploymentHurdleEditor */

interface HurdleEditorProps extends HurdleProps {
  loadApp: Function;
}

interface MilitaryServiceHurdleShape {
  ever_serve: boolean;
  active_duty_expiration: string;
  active_duty: boolean;
  retired_discharged_separated: boolean;
  non_activated_reserve: boolean;
  surviving_spouse: boolean;
}

const EmploymentHurdleEditor: React.FC<HurdleEditorProps> = ({ app, hurdle, loadApp, onHide }) => {
  const hasCoborrower = app.has_borrower2;

  // Initial fetching
  useEffect(() => {
    loadMilitaryServiceFromAPI(app.id, 1);
    if (hasCoborrower) {
      loadMilitaryServiceFromAPI(app.id, 2);
    }
  }, [app.id]);

  const [employmentFormState, updateEmploymentFormState] = useState(initialValues);

  // military service methods
  const processMilitaryServiceSuccessAPIResponse = (
    militaryService: MilitaryServiceShape,
    borrowerEntity: number,
  ) => {
    setMilitaryServiceStateForBorrowerFromAPI(borrowerEntity, militaryService);
    updateLoadingState(LoadingState.FINISHED);
  };

  const processMilitaryServiceFailureAPIResponse = (err: any, borrowerEntity: number) => {
    logException(err);
    if (err && err.meta && err.meta.error_detail) {
      updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, err.meta.error_detail);
    }
    updateLoadingState(LoadingState.FINISHED);
  };

  const isValidMilitaryServiceState = (stateForBorrower: MilitaryServiceHurdleShape) => {
    const hasServiceType =
      stateForBorrower.active_duty ||
      stateForBorrower.non_activated_reserve ||
      stateForBorrower.retired_discharged_separated ||
      stateForBorrower.surviving_spouse;

    if (!!stateForBorrower.ever_serve !== !!hasServiceType) {
      return "If indicating service, please specify type.";
    }

    if (stateForBorrower.active_duty && !stateForBorrower.active_duty_expiration) {
      return "Please specify active duty expiration date";
    }

    // the form state update handler manages this, so shouldn't be reached ever,
    // but still an invalid state
    if (!stateForBorrower.active_duty && stateForBorrower.active_duty_expiration) {
      return "If not on active duty, then should be no active duty expiration date";
    }

    // all good
    return "";
  };

  const persistMilitaryServiceForBorrowerAtAPI = (
    appID: number,
    borrowerEntity: number,
    militaryServiceStateForBorrower: MilitaryServiceHurdleShape,
  ) => {
    updateLoadingState(LoadingState.ACTIVE);

    return APIClient.post(
      `/uw/${appID}/military_service/${borrowerEntity}`,
      militaryServiceStateForBorrower,
    )
      .then((response: any) => {
        processMilitaryServiceSuccessAPIResponse(response.military_service, borrowerEntity);
        return true;
      })
      .catch(err => {
        processMilitaryServiceFailureAPIResponse(err, borrowerEntity);
        return false;
      });
  };

  // returns false if errors for either borrower, since in that case,
  // need to prevent reload -- so that error is displayed to operator.
  const persistMilitaryServiceAtAPI = async () => {
    const borrowersToCheck = [1, hasCoborrower && 2].filter(Boolean);
    const promises = borrowersToCheck.map(borrowerEntity => {
      if (!militaryServiceStateChanged[borrowerEntity]) {
        // no error, since nothing to persist
        return Promise.resolve(true);
      }

      const militaryServiceStateForBorrower: MilitaryServiceHurdleShape =
        militaryServiceState[borrowerEntity];
      const invalidMessage = isValidMilitaryServiceState(militaryServiceStateForBorrower);
      if (invalidMessage !== "") {
        updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, invalidMessage);
        return Promise.resolve(false);
      }
      return persistMilitaryServiceForBorrowerAtAPI(
        app.id,
        borrowerEntity,
        militaryServiceStateForBorrower,
      );
    });
    const values = await Promise.all(promises);
    return values.every(v => v);
  };

  const loadMilitaryServiceFromAPI = (appID: number, borrowerEntity: number) => {
    updateLoadingState(LoadingState.ACTIVE);

    APIClient.get(`/uw/${appID}/military_service/${borrowerEntity}`)
      .then((response: any) => {
        processMilitaryServiceSuccessAPIResponse(response.military_service, borrowerEntity);
      })
      .catch(err => {
        if (err && err.meta && err.meta.code === 404) {
          processMilitaryServiceSuccessAPIResponse(null, borrowerEntity);
        } else {
          processMilitaryServiceFailureAPIResponse(err, borrowerEntity);
        }
      });
  };

  const setMilitaryServiceStateForBorrowerFromAPI = (
    borrowerEntity: number,
    militaryService: MilitaryServiceShape,
  ) => {
    const existsOrDefault = (key: string, defaultValue: any) =>
      militaryService ? militaryService[key] : defaultValue;

    const militaryServiceForBorrower = {
      ever_serve: existsOrDefault("ever_serve", false),
      active_duty: militaryService ? militaryService.active_duty_expiration !== null : false, // set the one computed field
      active_duty_expiration: existsOrDefault("active_duty_expiration", null),
      retired_discharged_separated: existsOrDefault("retired_discharged_separated", false),
      non_activated_reserve: existsOrDefault("non_activated_reserve", false),
      surviving_spouse: existsOrDefault("surviving_spouse", false),
    };
    setInitialMilitaryServiceStateForBorrower(borrowerEntity, militaryServiceForBorrower);
  };

  const [militaryServiceState, updateMilitaryServiceState] = useState({
    1: {},
    ...(hasCoborrower && { 2: {} }),
  });

  // naive "do we need to update" / "was touched" boolean trackers.
  // just using to avoid those API update calls that are easily avoidable.
  const [militaryServiceStateChanged, updateMilitaryServiceStateChanged] = useState({
    1: false,
    ...(hasCoborrower && { 2: false }),
  });

  const setInitialMilitaryServiceStateForBorrower = (
    borrowerEntity: number,
    fullState: MilitaryServiceHurdleShape,
  ) => {
    updateMilitaryServiceState(prevState => ({
      ...prevState,
      [borrowerEntity]: fullState,
    }));
    updateMilitaryServiceStateChanged(prevState => ({
      ...prevState,
      [borrowerEntity]: false,
    }));
    updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, "");
  };

  const updateMilitaryServiceStateForBorrower = (borrowerEntity: number, change: object) => {
    updateMilitaryServiceState(prevState => ({
      ...prevState,
      [borrowerEntity]: {
        ...prevState[borrowerEntity],
        ...change,
      },
    }));
    updateMilitaryServiceStateChanged(prevState => ({
      ...prevState,
      [borrowerEntity]: true,
    }));
    updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, "");
  };

  const [militaryServiceErrorMessage, updateMilitaryServiceErrorMessage] = useState({
    1: "",
    ...(hasCoborrower && { 2: "" }),
  });

  const updateMilitaryServiceErrorMessageForBorrower = (
    borrowerEntity: number,
    message: string,
  ) => {
    updateMilitaryServiceErrorMessage(prevState => ({
      ...prevState,
      [borrowerEntity]: message,
    }));
  };
  // end military service methods

  // Modals
  const [employmentModalIsOpen, updateEmploymentModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  // Batch delete hooks
  const [batchDeleteModalIsOpen, setBatchDeleteModalIsOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const [editingMode, updateEditingMode] = useState(EditingState.CREATE);
  const [loadingState, updateLoadingState] = useState(LoadingState.INACTIVE);

  // Error message for the inline notification
  const [selectedDocId, updateSelectedDocId] = useState(null);

  const preparePayload = (values): any => ({
    ...values,
    self_employed_ownership_percent: values.self_employed_ownership_percent || null,
  });

  const createEmployment = values => {
    updateLoadingState(LoadingState.ACTIVE);

    const payload = preparePayload(values);

    // We're creating a new employment, so don't send ID
    delete payload.id;

    APIClient.post(`/uw/${app.id}/employments`, payload)
      .then(() => {
        updateLoadingState(LoadingState.FINISHED);
        updateEmploymentModalIsOpen(false);
        loadApp(app.id);
      })
      .catch(err => {
        updateLoadingState(LoadingState.FINISHED);
        // TODO: Add toast notifications (or something) indicating failure
      });
  };

  const deleteEmployment = employmentID => {
    APIClient.deleteRequest(`/uw/${app.id}/employments/${employmentID}`).then(() => {
      updateEmploymentFormState(initialValues);
      loadApp(app.id);
    });
  };

  const updateEmployment = values => {
    updateLoadingState(LoadingState.ACTIVE);

    const payload = preparePayload(values);

    APIClient.put(`/uw/${app.id}/employments`, payload)
      .then(() => {
        updateEmploymentFormState(initialValues);
        updateLoadingState(LoadingState.FINISHED);
        updateEmploymentModalIsOpen(false);
        loadApp(app.id);
      })
      .catch(err => {
        updateLoadingState(LoadingState.FINISHED);
      });
  };

  const ownerEntityChange = (doc, value) => {
    const payload = { ...doc };
    payload.owner_entity_type = value !== -1 ? "employment" : null;
    payload.owner_entity_id = value !== -1 ? value : null;

    APIClient.put(`/documents/${doc.id}`, payload).then(() => {
      updateSelectedDocId(null);
      loadApp(app.id);
    });
  };

  const updateMainIncome = (id: number, income_source: string, borrowerEntity: number) => {
    APIClient.put(`/loan_applications/${app.id}`, {
      [`borrower${borrowerEntity}_primary_income_id`]: id,
      [`borrower${borrowerEntity}_primary_income_source`]: income_source,
    }).then(() => {
      loadApp(app.id);
    });
  };

  const getW2s = () => {
    return app.documents.filter(d => d.type === DocTypes.W2);
  };

  const sortedEmployments = () => {
    return _.chain(app.employments)
      .sortBy("start_date")
      .sortBy(
        (employment: EmploymentShape) =>
          // make sure current employments are last
          employment.end_date || "9999-12-31",
      )
      .sortBy("borrower_entity")
      .value();
  };

  const employmentOptions = () => {
    const options = [
      {
        label: "No employment selected",
        value: -1,
      },
    ];
    return options.concat(
      _.map(sortedEmployments(), (employment: any) => {
        const start = employment.start_date ? employment.start_date.substr(0, 10) : "unknown";
        const end = employment.end_date ? employment.end_date.substr(0, 10) : "current";
        return {
          label: `${employment.name} (${start} to ${end})`,
          value: employment.id,
        };
      }),
    );
  };

  const toggleSelectedDocId = docId => {
    if (selectedDocId === null || selectedDocId !== docId) {
      updateSelectedDocId(docId);
    } else {
      updateSelectedDocId(null);
    }
  };

  const getW2Year = doc => findFieldValueForSection(doc, "main", "year");

  const textForDocs = (employment, year) => {
    const yearRequired = _.contains(employment.years_required_to_document, year);
    const yearMissing = _.contains(employment.years_needing_documentation, year);

    if (!yearRequired) {
      return "Not required";
    }

    if (!yearMissing) {
      return "Yes";
    }

    // it is required but it's missing
    return "No";
  };

  const onSave = async () => {
    if (!(await persistMilitaryServiceAtAPI())) {
      // if there was an error, return so that it is displayed to operator,
      // rather than page reloaded, which would clear error.
      return;
    }

    const promises = [];
    app.employments.forEach(employment => {
      if (!employmentFormState || !employmentFormState[employment.id]) {
        return; // since not edited, no need to hit API
      }

      if (employmentFormState[employment.id] && employmentFormState[employment.id].start_date) {
        employment.start_date = employmentFormState[employment.id].start_date;
      }
      if (
        employmentFormState[employment.id] &&
        employmentFormState[employment.id].hasOwnProperty("end_date")
      ) {
        if (employmentFormState[employment.id].end_date === "current") {
          employment.end_date = null;
        } else {
          employment.end_date = employmentFormState[employment.id].end_date;
        }
      }
      promises.push(APIClient.put(`/uw/${app.id}/employments`, employment));
    });
    Promise.all(promises)
      .then(() => {
        loadApp(app.id);
      })
      .catch((err: any) => {
        alert(err.meta.error_detail);
      });
  };

  const updateMixedUse = (v: string) => {
    updateLoadingState(LoadingState.ACTIVE);

    const is_mixed_use: boolean = v === "yes";
    const url = `/loan_applications/${app.id}/hurdles/operator_subject_property_type`;

    const data: UpdatePropertyTypeRequest = {
      is_mixed_use,
    };

    return APIClient.post(url, data).then(() => updateLoadingState(LoadingState.INACTIVE));
  };

  const renderMixedProperty = () => {
    const initialValue = app.property_is_mixed_use ? "yes" : "no";

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "50px",
          paddingLeft: "15px",
          marginRight: "1rem",
        }}
      >
        <p className={employmentHurdleStyles.question}>
          Will the borrower set aside space in the subject property to operate a business that
          customers visit:
          <LearnMore text="This should only be completed for owner-occupied properties. <br /><br /> Only select Yes if the property will have commercial space set aside such as a daycare, medical office, beauty/barber shop. <br /> <br />This should not be marked Yes for home offices used by remote employees." />
        </p>
        <RadioButtonGroup
          id="property-is-mixed-use"
          name="property_is_mixed_use"
          valueSelected={initialValue}
          onChange={value => updateMixedUse(value)}
        >
          <RadioButton value="yes" labelText="Yes" id="is_mixed" />
          <RadioButton value="no" labelText="No" id="is_not_mixed" />
        </RadioButtonGroup>
      </div>
    );
  };

  const renderMilitaryService = (borrowerEntity: number, borrowerName: string) => {
    const militaryServiceStateForBorrower = militaryServiceState
      ? militaryServiceState[borrowerEntity]
      : {};
    const militaryServiceErrorMessageForBorrower = militaryServiceErrorMessage
      ? militaryServiceErrorMessage[borrowerEntity]
      : "";

    return (
      <>
        <div
          style={{
            paddingLeft: "15px",
            marginRight: "1rem",
          }}
        >
          {militaryServiceErrorMessageForBorrower.length > 0 && (
            <InlineNotification
              kind="error"
              notificationType="inline"
              role="alert"
              statusIconDescription="Error"
              subtitle={militaryServiceErrorMessageForBorrower}
              title="Error"
              onCloseButtonClick={() => {
                updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, "");
              }}
            />
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "50px",
            }}
          >
            <p className={employmentHurdleStyles.question}>
              Borrower {borrowerEntity} - {borrowerName}: Military Service
              <LearnMore
                text={`This answers the question on the loan application: "Did the borrower (or deceased spouse) ever serve, or are they currently serving, in the United States Armed Forces?"`}
              />
            </p>
            <RadioButtonGroup
              id={`${borrowerEntity}-military-service`}
              name={`${borrowerEntity}-military-service`}
              valueSelected={militaryServiceStateForBorrower.ever_serve === true ? "yes" : "no"}
              onChange={value => {
                let change: any = {
                  ever_serve: value === "yes" ? true : false,
                };
                if (!change.ever_serve) {
                  // if setting as never served, clear sub-values
                  change = {
                    ...change,
                    active_duty: false,
                    active_duty_expiration: null,
                    retired_discharged_separated: false,
                    non_activated_reserve: false,
                    surviving_spouse: false,
                  };
                }
                updateMilitaryServiceStateForBorrower(borrowerEntity, change);
              }}
            >
              <RadioButton
                value="yes"
                labelText="Yes"
                id={`${borrowerEntity}-yes-military-service`}
              />
              <RadioButton value="no" labelText="No" id={`${borrowerEntity}-no-military-service`} />
            </RadioButtonGroup>
          </div>
          {militaryServiceStateForBorrower.ever_serve === true && (
            <>
              <p
                style={{
                  fontStyle: "italic",
                  marginBottom: "10px",
                }}
              >
                Check all that apply, then hit Save.
              </p>
              <CarbonTable>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          margin: "5px 0",
                        }}
                      >
                        <Checkbox
                          id={`${borrowerEntity}-active-duty`}
                          labelText="Currently serving on active duty with projected expiration date of service/tour"
                          checked={militaryServiceStateForBorrower.active_duty}
                          onChange={value => {
                            let change: any = {
                              active_duty: !militaryServiceStateForBorrower.active_duty,
                            };
                            if (!change.active_duty) {
                              // if setting as not on active duty, also
                              // clear the active duty expiration
                              change = {
                                ...change,
                                active_duty_expiration: null,
                              };
                            }
                            updateMilitaryServiceStateForBorrower(borrowerEntity, change);
                          }}
                        />
                        {militaryServiceStateForBorrower.active_duty && (
                          <DatePicker
                            id={`${borrowerEntity}-active-duty-expiration`}
                            datePickerType="single"
                            dateFormat="Y-m-d"
                            value={militaryServiceStateForBorrower.active_duty_expiration}
                            onChange={event => {
                              updateMilitaryServiceStateForBorrower(borrowerEntity, {
                                active_duty_expiration: moment(event[0]).format("YYYY-MM-DD"),
                              });
                            }}
                          >
                            <DatePickerInput
                              id={`${borrowerEntity}-active-duty-expiration-input-id`}
                              labelText=""
                              placeholder="yyyy-mm-dd"
                              type="text"
                              size="sm"
                            />
                          </DatePicker>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        id={`${borrowerEntity}-retired-discharged-separated`}
                        labelText="Currently retired, discharged, or separated from service"
                        checked={militaryServiceStateForBorrower.retired_discharged_separated}
                        onChange={value =>
                          updateMilitaryServiceStateForBorrower(borrowerEntity, {
                            retired_discharged_separated:
                              !militaryServiceStateForBorrower.retired_discharged_separated,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        id={`${borrowerEntity}-non-activated-reserve`}
                        labelText="Only period of service was a non-activated member of the Reserve on National Guard"
                        checked={militaryServiceStateForBorrower.non_activated_reserve}
                        onChange={value =>
                          updateMilitaryServiceStateForBorrower(borrowerEntity, {
                            non_activated_reserve:
                              !militaryServiceStateForBorrower.non_activated_reserve,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        id={`${borrowerEntity}-surviving-spouse`}
                        labelText="Surviving spouse"
                        checked={militaryServiceStateForBorrower.surviving_spouse}
                        onChange={value =>
                          updateMilitaryServiceStateForBorrower(borrowerEntity, {
                            surviving_spouse: !militaryServiceStateForBorrower.surviving_spouse,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </CarbonTable>
            </>
          )}
        </div>
      </>
    );
  };

  const getEffectiveStartAndEndDates = employment => {
    const { start_date } = employment;
    // if (state[employment.id] && state[employment.id].start_date) {
    //   start_date = state[employment.id].start_date;
    // }

    const end_date = employment.end_date || "current";
    // if (state[employment.id] && state[employment.id].end_date) {
    //   end_date = state[employment.id].end_date;
    // }

    return { start_date, end_date };
  };

  const renderEmploymentsAndW2s = () => {
    const w2s = getW2s();

    // the years to display in columns are based on the qualification
    // date for the application
    const qualificationDate = app.computed_properties
      ? app.computed_properties.qualification_date
      : app.created_at;
    const yearMinusOne = parseInt(qualificationDate.substr(0, 4), 10) - 1;
    const yearMinusTwo = yearMinusOne - 1;

    const employmentHeaderData: TableHeaderDataShape[] = [
      {
        header: "Employer",
        key: "name",
      },
      {
        header: "Employer address",
        key: "employer_address",
        style: { minWidth: "180px" },
      },
      {
        header: "Start Date",
        key: "start_date",
      },
      {
        header: "End Date",
        key: "end_date",
      },
      {
        header: "Income Type",
        key: "income_type",
      },
      {
        header: "Employment Type",
        key: "employment_type",
      },
      {
        header: "Title",
        key: "title",
      },
      {
        header: "Main Income",
        key: "is_main_income",
      },
    ];

    const renderEmploymentRow = (employment: EmploymentShape) => {
      const { start_date, end_date } = getEffectiveStartAndEndDates(employment);

      const {
        id,
        name,
        contact,
        address_line1,
        address_line2,
        city,
        state,
        postal_code,
        employment_type,
        income_type,
        title,
        years_in_line_of_work,
        has_latest_required_paystubs,
      } = employment;

      return {
        id: `${id}`,
        name,
        borrower_entity: (
          <span style={{ whiteSpace: "nowrap" }}>
            {employment.borrower_entity === 1 ? "Borrower 1" : "Borrower 2"}
          </span>
        ),
        employer_address: addressToPostalFormatStr(
          address_line1,
          address_line2,
          city,
          state,
          postal_code,
        ) as string,
        start_date: <span style={{ whiteSpace: "nowrap" }}>{start_date}</span>,
        end_date: <span style={{ whiteSpace: "nowrap" }}>{end_date}</span> || "current",
        title,
        is_main_income: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
            onClick={() =>
              updateMainIncome(employment.id, employment.income_type, employment.borrower_entity)
            }
            // Accessibility
            onKeyPress={() =>
              updateMainIncome(employment.id, employment.income_type, employment.borrower_entity)
            }
            tabIndex={0}
          >
            {app.borrower1_primary_income_id === employment.id ||
            app.borrower2_primary_income_id === employment.id ? (
              <Checkmark32 />
            ) : (
              <Button kind="tertiary" size="small" style={{ padding: "5px" }}>
                Set as Main Income
              </Button>
            )}
          </div>
        ),
        income_type: (
          <span style={{ fontWeight: "bold" }}>{incomeTypeToStr(income_type as IncomeType)}</span>
        ),
        employment_type: employment_type === EmploymentType.FullTime ? "Full time" : "Part time",

        expanded_data: (
          <div className={employmentHurdleStyles.expandedDataSection}>
            <LabeledObject
              title="Additional Employer Information"
              size="compact"
              listData={[
                {
                  label: "Employer Address",
                  value: addressToPostalFormatStr(
                    address_line1,
                    address_line2,
                    city,
                    state,
                    postal_code,
                  ) as string,
                },
                {
                  label: "Years In Line Of Work",
                  value: `${years_in_line_of_work}`,
                },
                {
                  label: "Phone #",
                  value: <span style={{ whiteSpace: "nowrap" }}>{contact}</span>,
                },
                {
                  label: `Docs for ${yearMinusTwo}`,
                  value: textForDocs(employment, yearMinusTwo),
                },
                {
                  label: `Docs for ${yearMinusOne}`,
                  value: textForDocs(employment, yearMinusOne),
                },
                {
                  label: "Has latest required paystubs",
                  value: has_latest_required_paystubs ? "Yes" : "No",
                },
              ]}
            />
          </div>
        ),
      };
    };

    // Little convenience list
    const borrowers = !app.has_borrower2 ? [1] : [1, 2];

    return (
      <div className="container-fluid">
        {app.employments && (
          <>
            <Modal
              danger
              open={deleteModalIsOpen}
              modalHeading="Are you sure you'd like to delete this record?"
              primaryButtonText="Delete"
              secondaryButtonText="Cancel"
              onBlur={() => setDeleteModalIsOpen(false)}
              onRequestClose={() => setDeleteModalIsOpen(false)}
              onRequestSubmit={() => deleteEmployment(employmentFormState.id)}
            />

            <Modal
              danger
              open={batchDeleteModalIsOpen}
              modalHeading="Confirm batch delete"
              primaryButtonText={`Delete ${selectedRows.length} employment${
                selectedRows.length > 1 ? "s" : ""
              }`}
              secondaryButtonText="Cancel"
              onBlur={() => setBatchDeleteModalIsOpen(false)}
              onRequestClose={() => setBatchDeleteModalIsOpen(false)}
              onRequestSubmit={() => {
                updateLoadingState(LoadingState.ACTIVE);

                const promises = selectedRows
                  .map(row => row.id)
                  .map(rowID => {
                    return APIClient.deleteRequest(`/uw/${app.id}/employments/${rowID}`).then(
                      () => {
                        updateEmploymentFormState(initialValues);
                      },
                    );
                  });

                Promise.all(promises).then(() => {
                  updateLoadingState(LoadingState.FINISHED);

                  // Just refresh after a batch delete to reload data
                  window.location.reload();
                });
              }}
            >
              <p style={{ fontSize: "18px", marginBottom: "10px" }}>
                The following employments will be deleted:{" "}
              </p>
              <ul>
                {selectedRows &&
                  selectedRows.map(employment => {
                    const { name, start_date, end_date, borrower_entity } = employment;
                    return (
                      <li key={name} style={{ marginLeft: "1rem" }}>
                        - <span style={{ fontWeight: "bold" }}>Borrower {borrower_entity}: </span>
                        {name} ({start_date} to {end_date || "current"})
                      </li>
                    );
                  })}
              </ul>
            </Modal>

            <Modal
              passiveModal
              open={employmentModalIsOpen}
              modalHeading={
                editingMode === EditingState.CREATE ? "Add Employment" : "Update Employment"
              }
              className={styles.patchModalBottomPaddingIssue}
              selectorPrimaryFocus="#employer-name"
              onRequestClose={() => {
                updateEmploymentFormState(initialValues);
                updateEmploymentModalIsOpen(false);
              }}
              onBlur={() => {
                updateEmploymentFormState(initialValues);
                updateEmploymentModalIsOpen(false);
              }}
            >
              {/* Loading spinner for table */}
              <Loading active={loadingState === LoadingState.ACTIVE} />

              <p>Required fields are marked with an asterisk *</p>
              <EmploymentModalForm
                onSubmit={(values: any) => {
                  if (editingMode === EditingState.CREATE) {
                    createEmployment(values);
                  }
                  if (editingMode === EditingState.UPDATE) {
                    updateEmployment(values);
                  }
                }}
                formState={employmentFormState} // Pass the form state for when we edit fields
                borrowers={app.borrowers}
                editingMode={editingMode}
                onRequestClose={() => {
                  updateEmploymentFormState(initialValues);
                  updateEmploymentModalIsOpen(false);
                }}
              />
            </Modal>
            {borrowers.map(borrowerID => {
              const employmentData = sortedEmployments().filter(
                (employment: EmploymentShape) => employment.borrower_entity === borrowerID,
              );
              return (
                <BaseTable
                  key={`borrower-employments-${borrowerID}`}
                  useExpandedRows
                  title={`Borrower ${borrowerID}: Employment History`}
                  emptyRowText="No Employments"
                  disableBatchEdit
                  allowHeaderTextWrapping
                  onBatchDelete={selectedRawRowsData => {
                    setBatchDeleteModalIsOpen(true);
                    setSelectedRows(selectedRawRowsData);
                  }}
                  openModal={() => updateEmploymentModalIsOpen(true)}
                  openDeleteModal={() => setDeleteModalIsOpen(true)}
                  updateFormState={data => updateEmploymentFormState(data)}
                  updateEditingMode={updateEditingMode}
                  headerData={employmentHeaderData}
                  rawRowData={employmentData}
                  rowData={employmentData.map(renderEmploymentRow)}
                />
              );
            })}
          </>
        )}
      </div>
    );
  };

  return (
    <HurdleView
      className={employmentHurdleStyles.employmentHurdle}
      app={app}
      hurdle={hurdle}
      onHide={onHide}
    >
      {renderEmploymentsAndW2s()}
      {renderMixedProperty()}
      {renderMilitaryService(1, `${app.borrower1_first_name} ${app.borrower1_last_name}`)}
      {hasCoborrower &&
        renderMilitaryService(2, `${app.borrower2_first_name} ${app.borrower2_last_name}`)}
      <footer className={styles.footer}>
        <Button kind="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button kind="primary" onClick={onSave} renderIcon={Save16}>
          Save changes
        </Button>
      </footer>
    </HurdleView>
  );
};

export default connect<any, any, any>(null, mapDispatchToProps)(EmploymentHurdleEditor);
